import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, Input, Space, Tag, message, Modal, Row, Col, Statistic, Empty, Popconfirm, Switch } from 'antd';
import { PlusOutlined, ReloadOutlined, LineChartOutlined, DeleteOutlined, MonitorOutlined, KeyOutlined, ThunderboltOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import { apiFetch, onDataChanged } from '../utils/api.js';
import { yuan } from '../utils/format.js';
import { BRAND } from '../utils/chartTheme.js';

export default function CompetitorMonitor() {
  const [list, setList] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [url, setUrl] = useState('');
  const [adding, setAdding] = useState(false);
  const [chartItem, setChartItem] = useState(null);
  const [history, setHistory] = useState([]);
  // key 配置
  const [cfg, setCfg] = useState({ configured: false, key_masked: '' });
  const [keyInput, setKeyInput] = useState('');
  const [cfgOpen, setCfgOpen] = useState(false);

  const loadCfg = useCallback(() => {
    apiFetch('/api/competitor-config').then(r => r.json()).then(d => setCfg(d.data || {})).catch(() => {});
  }, []);
  const loadData = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    apiFetch('/api/competitors').then(r => r.json()).then(d => {
      setList(d.data || []); setAlerts(d.alerts || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { loadData(); loadCfg(); }, [loadData, loadCfg]);
  useEffect(() => onDataChanged(() => { loadData(true); }), [loadData]);

  const saveKey = async () => {
    if (!keyInput.trim()) { message.warning('请粘贴 OneBound API Key'); return; }
    const res = await apiFetch('/api/competitor-config', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ oneboundKey: keyInput.trim() }) }).then(r => r.json());
    if (res.success) { message.success('Key 已保存'); setKeyInput(''); loadCfg(); } else message.error(res.error || '保存失败');
  };

  const addCompetitor = async () => {
    if (!url.trim()) { message.warning('请粘贴竞品商品链接'); return; }
    setAdding(true);
    try {
      const res = await apiFetch('/api/competitors', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ url: url.trim() }) }).then(r => r.json());
      if (res.success) { message.success(`已添加监控：${res.data.title || res.data.item_id}`); setUrl(''); loadData(true); }
      else message.error(res.error || '添加失败');
    } catch (e) { message.error('添加失败'); }
    setAdding(false);
  };

  const fetchAll = async () => {
    setFetching(true);
    try {
      const res = await apiFetch('/api/competitors/fetch', { method: 'POST' }).then(r => r.json());
      if (res.success) { message.success('抓取完成'); loadData(true); }
      else message.error(res.error || '抓取失败');
    } catch (e) { message.error('抓取失败'); }
    setFetching(false);
  };

  const del = async (id) => {
    const res = await apiFetch('/api/competitors/' + id, { method: 'DELETE' }).then(r => r.json());
    if (res.success) { message.success('已删除'); loadData(true); }
  };

  const openChart = async (item) => {
    setChartItem(item);
    const res = await apiFetch('/api/competitors/' + item.id + '/history').then(r => r.json()).catch(() => null);
    setHistory((res && res.data) || []);
  };

  const platformTag = (p) => p === 'taobao' ? <Tag color="#FF6000">淘宝</Tag> : <Tag color="#E02E24">拼多多</Tag>;
  const changeCell = (v, isPrice) => {
    if (!v) return <span style={{ color: '#bfbfbf' }}>-</span>;
    const up = v > 0;
    const color = isPrice ? (up ? '#CF1322' : '#3F8600') : (up ? '#3F8600' : '#CF1322'); // 价格涨=红(对手涨价对我好?) 销量涨=红(对手卖得好)
    return <span style={{ color }}>{up ? '↑' : '↓'}{isPrice ? '¥' + yuan(Math.abs(v)) : Math.abs(v)}</span>;
  };

  const columns = [
    { title: '平台', dataIndex: 'platform', width: 80, render: platformTag },
    { title: '竞品', dataIndex: 'title', ellipsis: true, render: (v, r) => v || r.item_id },
    { title: '当前价格', dataIndex: 'last_price', width: 100, align: 'right', render: v => v ? <span style={{ color: 'var(--brand)', fontWeight: 600 }}>¥{yuan(v)}</span> : '-' },
    { title: '销量', dataIndex: 'last_sales', width: 90, align: 'right', render: v => v ? v.toLocaleString() : '-' },
    { title: '评价数', dataIndex: 'last_reviews', width: 90, align: 'right', render: v => v ? v.toLocaleString() : '-' },
    { title: '价格变化', dataIndex: 'price_change', width: 100, align: 'right', render: v => changeCell(v, true) },
    { title: '销量变化', dataIndex: 'sales_change', width: 100, align: 'right', render: v => changeCell(v, false) },
    { title: '最近抓取', dataIndex: 'last_fetched_at', width: 150, render: v => v || '-' },
    {
      title: '操作', width: 130, render: (_, r) => (
        <Space size={4}>
          <Button size="small" type="link" icon={<LineChartOutlined />} onClick={() => openChart(r)}>走势</Button>
          <Popconfirm title="删除该竞品监控？" onConfirm={() => del(r.id)}><Button size="small" type="link" danger icon={<DeleteOutlined />} /></Popconfirm>
        </Space>
      )
    },
  ];

  const chartOption = () => {
    const dates = history.map(h => h.date + ' ' + String(h.fetched_at || '').slice(11, 16));
    return {
      tooltip: { trigger: 'axis' },
      legend: { data: ['价格', '销量'], top: 0 },
      grid: { left: 50, right: 50, top: 34, bottom: 24 },
      xAxis: { type: 'category', data: dates, axisLabel: { fontSize: 11 } },
      yAxis: [{ type: 'value', name: '价格', axisLabel: { formatter: '¥{value}' } }, { type: 'value', name: '销量' }],
      series: [
        { name: '价格', type: 'line', smooth: true, data: history.map(h => h.price), itemStyle: { color: BRAND }, areaStyle: { opacity: 0.08 } },
        { name: '销量', type: 'line', smooth: true, yAxisIndex: 1, data: history.map(h => h.sales), itemStyle: { color: '#185FA5' } },
      ],
    };
  };

  return (
    <div className="page-container">
      {/* OneBound key 配置 */}
      <Card size="small" style={{ marginBottom: 12 }}
        title={<span><KeyOutlined style={{ color: 'var(--brand)', marginRight: 8 }} />数据源配置（OneBound）{cfg.configured ? <Tag color="green" style={{ marginLeft: 8 }}>已配置 {cfg.key_masked}</Tag> : <Tag color="orange" style={{ marginLeft: 8 }}>未配置</Tag>}</span>}
        extra={<Button size="small" onClick={() => setCfgOpen(o => !o)}>{cfgOpen ? '收起' : '配置'}</Button>}>
        {cfgOpen ? (
          <div>
            <div style={{ color: '#8c8c8c', fontSize: 12, marginBottom: 8 }}>到 open.onebound.cn 注册拿 API Key（有免费试用），粘贴到下面即可启用真实数据抓取。</div>
            <Space><Input.Password style={{ width: 340 }} placeholder="粘贴 OneBound API Key" value={keyInput} onChange={e => setKeyInput(e.target.value)} /><Button type="primary" onClick={saveKey}>保存</Button></Space>
          </div>
        ) : (
          <span style={{ color: '#8c8c8c', fontSize: 12 }}>{cfg.configured ? '已配置数据源，可正常抓取竞品数据' : '未配置 API Key —— 点「配置」填入 OneBound Key（未配置时用官方测试 key，可能无数据）'}</span>
        )}
      </Card>

      {/* 添加竞品 */}
      <Card size="small" style={{ marginBottom: 12 }} title={<span><MonitorOutlined style={{ color: 'var(--brand)', marginRight: 8 }} />竞品监控</span>}
        extra={<Space><Button icon={<ThunderboltOutlined />} loading={fetching} onClick={fetchAll}>立即抓取</Button><Button icon={<ReloadOutlined />} onClick={() => loadData()}>刷新</Button></Space>}>
        <Space.Compact style={{ width: '100%', marginBottom: 12 }}>
          <Input placeholder="粘贴拼多多或淘宝竞品商品链接，如 https://mobile.yangkeduo.com/goods.html?goods_id=xxx 或 https://item.taobao.com/item.htm?id=xxx"
            value={url} onChange={e => setUrl(e.target.value)} onPressEnter={addCompetitor} />
          <Button type="primary" icon={<PlusOutlined />} loading={adding} onClick={addCompetitor}>添加监控</Button>
        </Space.Compact>
        <Table size="small" rowKey="id" loading={loading} columns={columns} dataSource={list}
          pagination={false} locale={{ emptyText: <Empty description="暂无监控竞品，粘贴商品链接添加" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }} />
      </Card>

      {/* 异常预警 */}
      <Card size="small" title={<span style={{ color: '#CF1322' }}>⚠️ 异常预警（降价/爆单/差评）</span>}>
        {alerts.length ? (
          <Table size="small" rowKey="id" dataSource={alerts} pagination={false}
            columns={[
              { title: '时间', dataIndex: 'time', width: 150 },
              { title: '平台', dataIndex: 'platform', width: 80, render: platformTag },
              { title: '竞品', dataIndex: 'title', ellipsis: true },
              { title: '异常', dataIndex: 'alert', render: v => <Tag color="red">{v}</Tag> },
            ]} />
        ) : <Empty description="暂无异常预警" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
      </Card>

      {/* 走势图 */}
      <Modal title={chartItem ? `走势：${chartItem.title || chartItem.item_id}` : ''} open={!!chartItem} onCancel={() => setChartItem(null)} footer={null} width={720}>
        {history.length ? <ReactECharts option={chartOption()} style={{ height: 360 }} /> : <Empty description="暂无历史数据，等待定时抓取" />}
      </Modal>
    </div>
  );
}
